/* Quick add, cart count and confirmation toast.
   Nothing here blocks first paint: no work happens until a button is pressed.

   Three things had to be handled properly rather than assumed:
   1. The rails auto-drift and are pointer-scrolled, so a press that turns into a drag must
      not add to the basket. We measure movement and bail past a small threshold.
   2. An add can fail — sold out between render and click, or the network drops. The toast
      says so and the button returns to its resting state rather than lying.
   3. The count is read back from Shopify rather than incremented locally, so it stays true
      even if something else changed the cart in another tab. */
(function () {
  /* Read by the ?cgdiag=1 self test in layout/theme.liquid to tell a fixed build from a
     cached one on a device this machine cannot inspect. Bump it with each cart fix. */
  window.__cgBuild = '2026-09-09c cart-fixes: qadd-choose, touch-slop, evidence-toast, consent-band, self-heal';
  var TOAST_MS = 3600;
  /* A mouse click moves 0px. A finger tap routinely moves 8 to 12, so one threshold
     cannot serve both: 6px read every real tap on a phone as a drag. */
  var DRAG_PX = 6;
  var TOUCH_DRAG_PX = 14;
  var toastEl, toastTimer;

  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'cg-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
    return toastEl;
  }

  function toast(message, ok) {
    var el = ensureToast();
    el.innerHTML =
      '<span class="cg-toast-ic" aria-hidden="true">' +
      (ok
        ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 7"/></svg>'
        : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 7v7M12 17.5v.5"/></svg>') +
      '</span><span class="cg-toast-t"></span>' +
      '<button class="cg-toast-a" type="button" data-cg-open-cart>View basket</button>';
    el.querySelector('.cg-toast-t').textContent = message;
    el.classList.toggle('cg-toast--bad', !ok);
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('is-on'); }, TOAST_MS);
  }

  function setCount(n) {
    document.querySelectorAll('#cartcount').forEach(function (b) {
      if (b.textContent.trim() === String(n)) return;
      b.textContent = n;
      b.classList.remove('is-bump');
      void b.offsetWidth;              // restart the animation
      b.classList.add('is-bump');
    });
    document.querySelectorAll('cart-bubble').forEach(function (el) {
      var t = el.querySelector('[data-cart-count], .cart-bubble__text');
      if (t) t.textContent = n;
    });
  }

  /* no-store because a cached /cart.js reports the pre-add count, and the badge is the
     confirmation — showing the old number is worse than showing nothing. The second read
     covers the case where the event lands fractionally before the cart is committed. */
  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  function resolveAddedName(sourceName, settledProductId, merchandiseIds, cart, renderedName) {
    if (sourceName) return String(sourceName).trim();
    var productId = settledProductId ? String(settledProductId) : '';
    if (!productId && cart && cart.items && merchandiseIds.length) {
      var line = cart.items.find(function (item) {
        return merchandiseIds.indexOf(String(item.variant_id)) > -1;
      });
      productId = line ? String(line.product_id) : '';
    }
    if (!productId) return '';
    return String(renderedName(productId) || '').trim();
  }

  /* The last count we have actually read from Shopify. The confirmation toast compares
     against it, so it has to be real rather than assumed. */
  var knownCount = null;

  function refreshCount() {
    return readCart().then(function (c) {
      setCount(c.item_count);
      knownCount = c.item_count;
      setTimeout(function () {
        readCart().then(function (c2) {
          if (c2.item_count !== c.item_count) { setCount(c2.item_count); knownCount = c2.item_count; }
        });
      }, 700);
      return c;
    });
  }

  /* Seed it once per page, after paint, so the first add of a session has something true to
     measure against. Also corrects a stale badge on a page served from cache. */
  function seedCount() {
    return readCart()
      .then(function (c) { setCount(c.item_count); knownCount = c.item_count; })
      .catch(function () {});
  }
  if (window.requestIdleCallback) requestIdleCallback(seedCount, { timeout: 2500 });
  else setTimeout(seedCount, 1200);

  var DRAWER_WRAP = 'shopify-section-cart-drawer-section';

  /* Pull a fresh render of the drawer and swap it in. Kept deliberately quiet: it runs
     after the toast, so nothing the shopper is looking at moves while they read it. */
  function redrawDrawer() {
    var host = document.getElementById(DRAWER_WRAP);
    if (!host) return Promise.resolve();
    return fetch('/?section_id=cart-drawer-section', { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('#' + DRAWER_WRAP) || tmp.firstElementChild;
        if (!fresh) return;
        var dlg = host.querySelector('dialog');
        if (dlg && dlg.open) return;   // never swap the panel out from under an open drawer
        host.innerHTML = fresh.innerHTML;
      })
      .catch(function () { /* a stale drawer is recoverable; a thrown error is not */ });
  }

  /* The variant ids a control will POST, or null when it carries none that Shopify would
     accept. Nothing may reach /cart/add.js without passing through here: Number(null) is 0
     and Number('') is 0, and Shopify answers an id of 0 with 422 "Cannot find variant",
     which is the error text a shopper actually read on their phone. */
  function addIds(btn) {
    var multi = btn.getAttribute('data-qadd-multi');
    var raw = multi
      ? multi.split(',').filter(function (x) { return String(x).trim() !== ''; })
      : [btn.getAttribute('data-qadd')];
    var ids = [];
    for (var i = 0; i < raw.length; i++) {
      var n = Number(String(raw[i] == null ? '' : raw[i]).trim());
      if (!n || !isFinite(n) || n <= 0) return null;   // one bad id invalidates the control
      ids.push(n);
    }
    return ids.length ? ids : null;
  }

  function add(btn) {
    if (btn.dataset.busy) return;
    var multi = btn.getAttribute('data-qadd-multi');
    var ids = addIds(btn);
    if (!ids) { toast('Sorry, that could not be added', false); return; }
    btn.dataset.busy = '1';
    btn.classList.add('is-busy');
    var title = btn.getAttribute('data-title') || 'Item';
    /* F065: the Delivery line-item property travels with every add route, not only the
       product form. Cards carry data-delivery; set buttons carry data-qadd-delivery, one
       entry per id in the same order, '|' separated. */
    var dl = multi ? (btn.getAttribute('data-qadd-delivery') || '').split('|')
                   : [btn.getAttribute('data-delivery') || ''];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: ids.map(function (i, k) {
          var it = { id: i, quantity: 1 };
          if (dl[k]) it.properties = { Delivery: dl[k] };
          return it;
        })
      })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message || 'Could not add'); });
        return r.json();
      })
      .then(function () {
        btn.classList.add('is-done');
        setTimeout(function () { btn.classList.remove('is-done'); }, 1400);
        window.__cgSuppressToast = true;
        toast(title + ' added to your basket', true);
        /* Horizon's cart component re-renders from rendered section HTML carried on its
           own event. A synthetic event has none, so the drawer stayed a step behind and
           opening it after a quick add showed the previous cart. Re-render the section
           through the Section Rendering API instead, which is the supported path. */
        redrawDrawer();
        return refreshCount();
      })
      .catch(function (err) {
        toast(err.message || 'Sorry, that could not be added', false);
      })
      .finally(function () {
        delete btn.dataset.busy;
        btn.classList.remove('is-busy');
      });
  }

  /* Opening the basket must not cost a page load. Losing the grid, the scroll position
     and the filters to look at what you just added is the single easiest way to end a
     session. Falls back to /cart only if the drawer is not on the page. */
  document.addEventListener('click', function (e) {
    var open = e.target.closest && e.target.closest('[data-cg-open-cart]');
    if (!open) return;
    e.preventDefault();
    var opener = document.querySelector('#opencart');
    if (opener) { opener.click(); if (toastEl) toastEl.classList.remove('is-on'); }
    else window.location.href = '/cart';
  });

  /* A press that becomes a drag is a scroll, not an add.

     Two faults lived here until 2026-09-09 and together they made the basket unusable on a
     phone, which is every visitor a Shopping ad sends.

     1. The pointermove listener was global. It measured every move anywhere on the page
        against downX/downY whether or not the press had begun on an add button, so `moved`
        was usually already true before a button was ever touched.
     2. DRAG_PX was 6, and a real finger tap moves more than that. Reproduced live: an 8px
        tap on a valid Add button left the cart at its previous count with no toast and no
        navigation. The button simply did nothing, which is exactly what was reported.

     A scripted .click() dispatches no pointer events at all, which is why this survived
     every harness that had ever been pointed at it. Arm only from a press that landed on an
     add control, remember which control it was, and give touch the slop a finger needs. */
  var dragFrom = null, downX = 0, downY = 0, moved = false;
  document.addEventListener('pointerdown', function (e) {
    var b = e.target.closest && e.target.closest('.qadd,[data-qadd],[data-qadd-multi]');
    dragFrom = b || null;
    if (!b) return;
    downX = e.clientX; downY = e.clientY; moved = false;
  }, { passive: true });

  document.addEventListener('pointermove', function (e) {
    if (!dragFrom) return;
    var slop = e.pointerType === 'mouse' ? DRAG_PX : TOUCH_DRAG_PX;
    if (Math.abs(e.clientX - downX) > slop || Math.abs(e.clientY - downY) > slop) moved = true;
  }, { passive: true });

  document.addEventListener('pointercancel', function () { dragFrom = null; moved = false; }, { passive: true });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.qadd,[data-qadd],[data-qadd-multi]');
    if (!btn) return;
    /* .qadd--choose is an anchor to the product page, not an add button: a product with a
       size or a finish must not have a variant guessed for it. It carries .qadd for the
       shared styling and no id. Matching it here meant preventDefault killed the navigation
       and then add() POSTed id 0, so the shopper got "Cannot find variant" and stayed on
       the grid. 19 of the 24 controls on the first screen of /collections/all are these.
       Anything with no usable id is not ours: leave the browser to it. */
    if (!addIds(btn)) return;
    e.preventDefault();
    e.stopPropagation();
    var wasDrag = moved && dragFrom === btn;
    dragFrom = null; moved = false;
    if (wasDrag) return;
    add(btn);
  });

  /* Anything else that changes the cart — the product form, the drawer, a quantity
     stepper — publishes Shopify's standard event. We listen rather than intercept, so
     Horizon keeps ownership of line items, selling plans and properties, and we only
     add the confirmation it never gave. */
  document.addEventListener('shopify:cart:lines-update', function (e) {
    var before = knownCount;
    var eventTarget = e && e.target;
    var eventForm = eventTarget && eventTarget.closest ? eventTarget.closest('product-form-component') : null;
    var eventQuickAdd = eventTarget && eventTarget.closest ? eventTarget.closest('quick-add-component') : null;
    var eventNameNode = eventForm && eventForm.querySelector ? eventForm.querySelector('[data-cg-name]') : null;
    var eventDisplayName = eventNameNode ? eventNameNode.getAttribute('data-cg-name') : null;
    if (!eventDisplayName && eventQuickAdd) eventDisplayName = eventQuickAdd.getAttribute('data-product-title');
    var merchandiseIds = e && e.action === 'add' && Array.isArray(e.lines)
      ? e.lines.map(function (line) { return String(line.merchandiseId); })
      : [];
    /* Horizon dispatches this event OPTIMISTICALLY: product-form.js builds it and fires it
       before it calls fetch(). Reading the cart on the event alone therefore races the
       write. The event carries its own settlement promise, which is what cart-drawer.js
       already waits on, so wait for it too when it is there and fall back to reading the
       cart twice when it is not. */
    var errored = false;
    var settledProductId = '';
    var settled = (e && e.promise && typeof e.promise.then === 'function')
      ? e.promise.then(
          function (res) {
            if (res && res.detail && res.detail.didError) errored = true;
            if (res && res.detail && res.detail.productId) settledProductId = String(res.detail.productId);
          },
          function () { errored = true; })
      : Promise.resolve();
    settled.then(function () { return refreshCount(); }).then(function (cart) {
      if (window.__cgSuppressToast) { window.__cgSuppressToast = false; return; }

      /* Until 2026-09-09 this said "added to your basket" on the event alone. The event
         fires whether or not the line landed, and it fires for removals and quantity
         changes too, so a failed add still told the shopper it had worked and they went to
         a basket that did not contain it. That is the "it says it added it but it isn't
         there" report. The cart is the only evidence there is, so read it and say what
         actually changed. A first read can beat the commit, hence the second look before
         calling anything a failure. */
      /* Only ever call something a failure on POSITIVE evidence of one, which means the
         event's own promise rejecting or reporting didError. An unchanged count is not
         evidence: Horizon can dispatch this event more than once for a single add, and the
         second pass sees a count that has already moved and would cry failure over a line
         that landed perfectly. Shipped that version by mistake earlier today and it told
         Danny an add had failed when it had not. When there is no promise to judge by and
         the count did not move, say nothing at all. */
      function verdict(count) {
        if (errored) return 'failed';
        if (before === null) return 'added';        // nothing true to compare against yet
        if (count > before) return 'added';
        if (count < before) return 'removed';
        return 'unknown';
      }

      function say(v, c) {
        if (v === 'removed' || v === 'unknown') return;   // the drawer already shows the truth
        if (v === 'failed') { toast('That could not be added. Please try again.', false); return; }
        var name = resolveAddedName(eventDisplayName, settledProductId, merchandiseIds, c, function (productId) {
          var rendered = document.querySelector('[data-product-id="' + productId + '"] .cart-items__title');
          return rendered ? rendered.textContent : '';
        });
        toast(name ? name + ' added to your basket' : 'Added to your basket', true);
      }

      var v = verdict(cart.item_count);
      if (v !== 'unknown') return say(v, cart);
      /* count had not moved yet and nothing reported an error: look once more before
         deciding, then stay quiet if it still has not. */
      return new Promise(function (r) { setTimeout(r, 800); })
        .then(readCart)
        .then(function (c2) {
          setCount(c2.item_count); knownCount = c2.item_count;
          say(verdict(c2.item_count), c2);
        })
        .catch(function () {});
    });
  });

  /* Save for later. Amazon's "Add to List" needs an account; this keeps the same
     affordance without one by storing handles on the device. Honest about what it is:
     the list is local, so nothing is promised that a logged-out shopper would not get. */
  var SAVE_KEY = 'cheragh:saved';
  function readSaved() {
    try { return JSON.parse(localStorage.getItem(SAVE_KEY) || '[]'); } catch (e) { return []; }
  }
  function paintSaved() {
    var saved = readSaved();
    document.querySelectorAll('[data-cg-save]').forEach(function (b) {
      var on = saved.indexOf(b.getAttribute('data-cg-save')) > -1;
      b.classList.toggle('is-saved', on);
      var t = b.querySelector('.savebtn-t');
      if (t) t.textContent = on ? 'Saved' : 'Save for later';
    });
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-cg-save]');
    if (!b) return;
    e.preventDefault();
    var handle = b.getAttribute('data-cg-save');
    var saved = readSaved();
    var i = saved.indexOf(handle);
    if (i > -1) { saved.splice(i, 1); } else { saved.push(handle); }
    try { localStorage.setItem(SAVE_KEY, JSON.stringify(saved)); } catch (err) {}
    paintSaved();
    toast(i > -1 ? 'Removed from your saved list'
                 : (b.getAttribute('data-name') || 'Item') + ' saved for later', true);
  });
  if (document.readyState === 'loading') addEventListener('DOMContentLoaded', paintSaved);
  else paintSaved();

  /* Consent banner offset. 2026-09-09.
     Shopify's own cookie banner (.shopify-pc__banner__dialog) mounts position:fixed at
     z-index 2000000. On a 390x844 phone it measured y=398 to y=844: 53 per cent of the
     screen, anchored to the bottom. Everything of ours in that band became untappable,
     including the sticky Add to basket bar at z-index 80 and this file's own toast.
     elementFromPoint across the Add to basket button returned the banner's own
     "manage preferences" button at 15 of 15 sample points.

     Every visitor arriving from a Shopping ad is a first-time visitor, so every one of
     them met the banner before they met the buy button.

     We do not raise our z-index above it. The shopper has to be able to answer the consent
     prompt, and covering it would be both hostile and non-compliant. Instead publish its
     height as --cg-consent-h and let our fixed furniture sit above it. */
  var consentRaf = 0;
  function measureConsent() {
    consentRaf = 0;
    /* Every candidate, not the first. Shopify leaves a hidden .shopify-pc__banner__dialog
       in the DOM once the preferences dialog has been opened, and querySelector returns
       that one because it comes first in document order. Measuring it reported a height of
       0 while the live banner was up, which is the same silent wrong answer the whole
       consent problem started as. Take the tallest one that is actually on screen. */
    var nodes = document.querySelectorAll('.shopify-pc__banner__dialog, .shopify-pc__banner');
    var h = 0;
    for (var i = 0; i < nodes.length; i++) {
      var b = nodes[i];
      var cs = getComputedStyle(b), r = b.getBoundingClientRect();
      var onScreen = cs.display !== 'none' && cs.visibility !== 'hidden' &&
                     cs.opacity !== '0' && r.height > 0 &&
                     r.bottom > window.innerHeight - 8 && r.top < window.innerHeight;
      if (onScreen) h = Math.max(h, Math.ceil(r.height));
    }
    document.documentElement.style.setProperty('--cg-consent-h', h ? h + 'px' : '0px');
    /* The variable alone only moves position:fixed furniture. Controls in normal flow -
       #cg-atc on the product page, every .qadd in the grid - need the page itself to be
       longer by the banner's height, or they can come to rest under it at the scroll end
       with no way to reach them. cheragh-artifact.css keys that padding off this
       attribute, so it appears and disappears with the banner. */
    if (h) document.documentElement.setAttribute('data-cg-consent', '1');
    else document.documentElement.removeAttribute('data-cg-consent');
  }
  function queueConsent() {
    if (consentRaf) return;
    consentRaf = requestAnimationFrame(measureConsent);
  }
  queueConsent();
  addEventListener('resize', queueConsent, { passive: true });
  addEventListener('orientationchange', queueConsent, { passive: true });
  /* the banner is injected and later removed by Shopify's own script, so watch for both */
  try {
    new MutationObserver(queueConsent).observe(document.documentElement,
      { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
  } catch (e) {}
  /* accepting or declining tears it down without a mutation we can rely on catching first.
     2026-09-15: one retry at 60ms was too early, and childList alone was blind to it. Shopify's
     Decline fades the banner out over longer than 60ms and hides it by attribute rather than by
     removal, so the sticky bar kept the 223px it had been lifted by after the banner was gone
     (GPT-6 Astra reproduced it at 390x844). Observe attributes too, retry on a ladder, and
     re-measure when any transition inside the banner ends. */
  function consentLadder() { [60, 300, 800, 1500, 3000].forEach(function (ms) { setTimeout(queueConsent, ms); }); }
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[class*="shopify-pc__"]')) consentLadder();
  }, true);
  document.addEventListener('transitionend', function (e) {
    if (e.target && e.target.closest && e.target.closest('[class*="shopify-pc__"]')) queueConsent();
  }, true);

  /* ------------------------------------------------------------------ stale build self heal
     Shopify sends storefront HTML with no Cache-Control header, so Safari heuristically
     caches the document; the document names an asset URL that is itself cached for a year.
     A returning visitor therefore keeps running whatever build they first loaded, and no
     fix ever reaches them. Confirmed live 2026-09-09: the site behaved correctly in a
     private tab and incorrectly in a normal one on the same phone.

     /cart is dynamic and never cached, so templates/cart.cgbuild.liquid reports the current
     asset version there. Compare it with the version actually running. On a mismatch, refetch
     this document with cache:'reload' - which replaces the browser's stored copy rather than
     merely bypassing it - and reload.

     Guarded on the server's own version in sessionStorage, so a given build can trigger at
     most one refresh per session and a wrong comparison can never loop.

     Send no Accept of our own. Shopify varies /cart on Accept and answers 404 to
     Accept: text/plain, which this used to send: measured 2026-09-11 on one URL, text/plain
     gave 404 while text/html, a wildcard, and no header at all all gave 200. r.ok was false
     every time, so the comparison below never ran once in production and the whole self
     heal was dead. Do not write a wildcard Accept literally in this comment: the slash-star
     pair closes the block and breaks the file. */
  function selfHeal() {
    var tag = document.querySelector('script[src*="cheragh-cart.js"]');
    var mine = tag ? (String(tag.src).split('?v=')[1] || '').split('&')[0] : '';
    if (!mine) return;
    fetch('/cart?view=cgbuild&t=' + (+new Date()), { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (live) {
        if (!live) return;
        live = live.trim();
        if (!/^[0-9]+$/.test(live) || live === mine) return;
        var key = 'cg:healed';
        try { if (sessionStorage.getItem(key) === live) return; sessionStorage.setItem(key, live); }
        catch (e) { return; }                       // no storage means no loop protection
        return fetch(location.href, { cache: 'reload' })
          .then(function () { location.reload(); })
          .catch(function () {});
      })
      .catch(function () {});
  }
  if (window.requestIdleCallback) requestIdleCallback(selfHeal, { timeout: 4000 });
  else setTimeout(selfHeal, 2000);

  /* Safari restores a whole page, JS heap included, from its back/forward cache with no
     network request at all, so a build can come back from the dead on a swipe back. */
  addEventListener('pageshow', function (e) { if (e.persisted) selfHeal(); });

  document.addEventListener('cart:update', refreshCount);
  document.addEventListener('visibilitychange', function () { if (!document.hidden) refreshCount(); });
})();
